import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    FormControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { SnackBarService } from 'app/core/snack-bar/snack-bar.service';
import { ScanQrcodeComponent } from 'app/modules/shared/containers/scan-qrcode/scan-qrcode.component';
import {
    QRCodeType,
    uuidRegExp,
    imeiRegExp,
    validateQrCode,
} from 'app/modules/shared/models/common';
import { CommonService } from 'app/modules/shared/services/common';
import { ContactsService } from '../../personal-settings/contacts/contacts.service';
import { Subject, take, takeUntil } from 'rxjs';
import { AccountSelectorService } from '../../account-selector/account-selector.service';
import { Router } from '@angular/router';
import { UserTableService } from '../user-table.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { partnerId } from 'app/core/auth/auth.config';

@Component({
    selector: 'app-add-user',
    imports: [
        FormsModule,
        MatButton,
        MatError,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatLabel,
        MatSelectModule,
        ReactiveFormsModule,
        TranslocoDirective,
        MatCheckbox,
    ],
    templateUrl: './add-user.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './add-user.component.scss'
})
export class AddUserComponent implements OnInit, OnDestroy {
    userForm: UntypedFormGroup;
    // languageCtrl = new FormControl('german');
    partnerIds = ['AP', 'JUH', 'MHD', 'BEWO'];

    contactsCtrl = new FormControl([]);

    contacts = [];

    // showPartnerId = true;
    partnerId = 'AP';

    activeUser: User = null;

    deviceDetails = null;

    private unsubscribe$: Subject<any> = new Subject<any>();

    getContact = (id: number) =>
        id ? this.getName(this.contacts.find((c) => c.id === id)) : '';

    getName = (contact) =>
        contact ? contact.firstName + ' ' + contact.lastName : '';

    constructor(
        private _matDialog: MatDialog,
        private _formBuilder: UntypedFormBuilder,
        private _snackBarService: SnackBarService,
        private _commonService: CommonService,
        private _contactsService: ContactsService,
        private _utService: UserTableService,
        private _accountSelector: AccountSelectorService,
        private _userService: UserService,
        private _router: Router,
    ) {}

    ngOnInit(): void {
        this._userService.activeUser$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((user) => {
                this.activeUser = user;
            });

        this._contactsService
            .getInternalContacts()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((contacts) => {
                this.contacts = contacts;
            });

        this._accountSelector.patients$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((patients) => {
                if (patients?.length === 0) {
                    this._router.navigate(['configuration-user/personal']);
                }
            });

        this.userForm = this._formBuilder.group({
            partnerId: [this.partnerId || null, [Validators.required]],
            firstName: [null, Validators.maxLength(50)],
            lastName: [null, Validators.maxLength(50)],
            email: [
                null,
                [
                    Validators.minLength(5),
                    Validators.maxLength(254),
                    Validators.email,
                ],
            ],
            password: [null, [Validators.minLength(8)]],
            // passwordForm: this._formBuilder.group(
            //     {
            //         password: [
            //             '',
            //             [Validators.required, Validators.minLength(8)],
            //         ],
            //         repeatPassword: ['', Validators.required],
            //     },
            //     { validators: confirmPasswordValidator }
            // ),
            withDrinkReminder: [true],
            withStepReminder: [true],
            additionalInternalContacts: this.contactsCtrl,
            imei: [null, [validateQrCode]],
            externalId: [null],
        });

        this.userForm
            .get('imei')
            .valueChanges.pipe(takeUntil(this.unsubscribe$))
            .subscribe((imei) => {
                this.qrScannerValidation(imei);
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    openAddDeviceWithQRcodeDialog() {
        // Open the dialog
        // const dialogRef = this._matDialog.open(AddDeviceWithQRcodeComponent, {
        //     width: '700px',
        // });
        const dialogRef = this._matDialog.open(ScanQrcodeComponent, {
            width: '700px',
        });

        dialogRef.afterClosed().subscribe((qrString) => {
            console.log('Compose dialog was closed!');
            if (qrString) {
                qrString = qrString.replaceAll(' ', '');
                this.qrScannerValidation(qrString);
                this.userForm.get('imei').setValue(qrString);
            }
        });
    }

    qrScannerValidation(qrString: string) {
        console.log('QR Result String:' + ' ' + qrString);
        const qrType = this.identifyScannedString(qrString);
        console.log(qrType);
        switch (qrType) {
            case QRCodeType.imeiQR:
                console.log('imei');
                this.fetchDeviceDetail(qrString);
                break;
            case QRCodeType.uuidQR:
                console.log('uuid');
                break;
            case QRCodeType.unknownQR:
                // this._snackBarService.openSnackBar(
                //     this._commonService.t('UNKNOWN-QR-CODE')
                // );
                this.deviceDetails = null;
                break;
        }
    }

    identifyScannedString(qrString: string): QRCodeType {
        if (uuidRegExp.test(qrString)) {
            return QRCodeType.uuidQR;
        }
        if (imeiRegExp.test(qrString)) {
            return QRCodeType.imeiQR;
        }
        return QRCodeType.unknownQR;
    }

    fetchDeviceDetail(imei: string) {
        this._utService
            .deviceByIMEI(imei)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data) => {
                this.deviceDetails = data;
                if (
                    this.deviceDetails?.number &&
                    this.activeUser.partnerId === 'JUH'
                ) {
                    const externalId = this.deviceDetails.number.replace(
                        '+',
                        '',
                    );
                    this.userForm.get('externalId').setValue(externalId);
                    this.userForm.get('externalId').disable();
                } else {
                    this.userForm.get('externalId').enable();
                }
            });
    }

    checkDeviceValidation() {
        const qrString = this.userForm.get('imei').value;
        if (qrString) {
            const qrType = this.identifyScannedString(qrString);
            if (qrType == QRCodeType.unknownQR) {
                this._snackBarService.openSnackBar(
                    this._commonService.t('UNKNOWN-QR-CODE'),
                );
                return false;
            }
        } else {
            return true;
        }
        if (!this.deviceDetails || !this.deviceDetails.number) {
            this._snackBarService.openSnackBar(
                this._commonService.t(
                    'UNKNOWN-DEVICE-UNABLE-TO-FIND-THIS-DEVICE-IN-THE-SYSTEM',
                ),
            );
            return false;
        } else if (this.deviceDetails?.isPaired == true) {
            this._snackBarService.openSnackBar(
                this._commonService.t(
                    'DEVICE-IS-ALREADY-ASSIGNED-TO-ANOTHER-USER',
                ),
            );
            return false;
        }
        return true;
    }

    /**
     * Send the form & close
     */
    save(): void {
        console.log(this.userForm.getRawValue());

        // format Authorities List
        let form = this.userForm.getRawValue();

        const email = form.email;
        const brandName = partnerId() === 'AP' ? 'ADELE' : 'JOHANNA';
        if (email?.length) {
            // Check email is exist in ALMA system.
            this._contactsService
                .findUserByEmail(email)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((users) => {
                    if (users?.length) {
                        let msg = this._commonService.t(
                            'ALREADY_AVAILABLE_EMAIL',
                        );
                        msg = msg.replace('BRAND_NAME', brandName);
                        this._snackBarService.openSnackBar(msg);
                        return;
                    } else {
                        this.saveUser();
                    }
                });
        } else {
            this.saveUser();
        }
    }

    saveUser() {
        let form = this.userForm.getRawValue();
        const deviceValidation = this.checkDeviceValidation();
        if (form && deviceValidation) {
            this._utService
                .createQuickUser(form)
                .pipe(take(1))
                .subscribe((isCreated) => {
                    if (isCreated == false) {
                        // this.showSnackbar('USER-NOT-CREATED');
                    } else {
                        console.log(isCreated);
                        this._router.navigate(['configuration-user/personal']);
                        // this.showSnackbar('USER-CREATED');
                        this._accountSelector
                            .getAccounts()
                            .pipe(take(1))
                            .subscribe();
                        // this._changeDetectorRef.markForCheck();
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
